import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Mic, Square, Play, Pause, Trash2, Download, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface EnhancedVoiceRecorderProps {
  onRecordingComplete: (blob: Blob, url: string) => void;
  onRecordingDelete?: () => void;
  maxDuration?: number; // in seconds
  allowUpload?: boolean;
}

export default function EnhancedVoiceRecorder({ 
  onRecordingComplete, 
  onRecordingDelete,
  maxDuration = 300, // 5 minutes default
  allowUpload = true 
}: EnhancedVoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string>('');
  const [audioLevel, setAudioLevel] = useState(0);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  
  const { toast } = useToast();

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      // Set up audio analysis for visual feedback
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      
      const updateAudioLevel = () => {
        if (analyserRef.current && isRecording) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
          setAudioLevel(Math.min(100, (average / 255) * 100));
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        }
      };
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        onRecordingComplete(blob, url);
        
        // Clean up
        stream.getTracks().forEach(track => track.stop());
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
      
      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      
      updateAudioLevel();
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= maxDuration) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
      toast({
        title: "Recording started",
        description: "Speak clearly into your microphone",
      });
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Recording failed",
        description: "Could not access microphone. Please check permissions.",
        variant: "destructive",
      });
    }
  }, [isRecording, maxDuration, onRecordingComplete, toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setAudioLevel(0);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      toast({
        title: "Recording completed",
        description: `Recorded ${recordingTime} seconds of audio`,
      });
    }
  }, [isRecording, recordingTime, toast]);

  const playRecording = useCallback(() => {
    if (recordedUrl && !isPlaying) {
      audioPlayerRef.current = new Audio(recordedUrl);
      audioPlayerRef.current.play();
      setIsPlaying(true);
      
      audioPlayerRef.current.onended = () => {
        setIsPlaying(false);
      };
    } else if (audioPlayerRef.current && isPlaying) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    }
  }, [recordedUrl, isPlaying]);

  const deleteRecording = useCallback(() => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl('');
    setRecordingTime(0);
    setIsPlaying(false);
    
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    
    onRecordingDelete?.();
    
    toast({
      title: "Recording deleted",
      description: "The audio recording has been removed",
    });
  }, [recordedUrl, onRecordingDelete, toast]);

  const downloadRecording = useCallback(() => {
    if (recordedBlob) {
      const url = URL.createObjectURL(recordedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `affirmation-${Date.now()}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Download started",
        description: "Your recording is being downloaded",
      });
    }
  }, [recordedBlob, toast]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('audio/')) {
      const url = URL.createObjectURL(file);
      setRecordedBlob(file);
      setRecordedUrl(url);
      onRecordingComplete(file, url);
      
      toast({
        title: "Audio uploaded",
        description: "Your audio file has been loaded",
      });
    } else {
      toast({
        title: "Invalid file",
        description: "Please upload an audio file",
        variant: "destructive",
      });
    }
  }, [onRecordingComplete, toast]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = (recordingTime / maxDuration) * 100;

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Voice Recording</h4>
        {recordedBlob && (
          <Badge variant="secondary">
            {formatTime(recordingTime)}
          </Badge>
        )}
      </div>

      {/* Recording Controls */}
      <div className="flex items-center gap-2">
        {!isRecording && !recordedBlob && (
          <Button onClick={startRecording} className="flex items-center gap-2">
            <Mic className="w-4 h-4" />
            Start Recording
          </Button>
        )}
        
        {isRecording && (
          <Button onClick={stopRecording} variant="destructive" className="flex items-center gap-2">
            <Square className="w-4 h-4" />
            Stop Recording
          </Button>
        )}
        
        {recordedBlob && !isRecording && (
          <>
            <Button onClick={playRecording} variant="outline" className="flex items-center gap-2">
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
            
            <Button onClick={downloadRecording} variant="outline" size="sm">
              <Download className="w-4 h-4" />
            </Button>
            
            <Button onClick={deleteRecording} variant="outline" size="sm">
              <Trash2 className="w-4 h-4" />
            </Button>
            
            <Button onClick={() => { deleteRecording(); }} variant="ghost" size="sm">
              Record Again
            </Button>
          </>
        )}
        
        {allowUpload && !recordedBlob && (
          <div>
            <input
              type="file"
              accept="audio/*"
              onChange={handleFileUpload}
              className="hidden"
              id="audio-upload"
            />
            <Button variant="outline" asChild>
              <label htmlFor="audio-upload" className="flex items-center gap-2 cursor-pointer">
                <Upload className="w-4 h-4" />
                Upload Audio
              </label>
            </Button>
          </div>
        )}
      </div>

      {/* Recording Progress */}
      {isRecording && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Recording... {formatTime(recordingTime)}</span>
            <span className="text-muted-foreground">Max: {formatTime(maxDuration)}</span>
          </div>
          <Progress value={progressPercentage} className="w-full" />
          
          {/* Audio Level Indicator */}
          <div className="flex items-center gap-2">
            <Mic className="w-4 h-4" />
            <div className="flex-1 bg-background border rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-100"
                style={{ width: `${audioLevel}%` }}
              />
            </div>
          </div>
        </div>
      )}
      
      {recordingTime >= maxDuration && (
        <p className="text-sm text-orange-600">
          Maximum recording time reached. Recording will stop automatically.
        </p>
      )}
    </Card>
  );
}